// Pure helpers for the portal's live-preview relay (#262): building the camera popup URL from a
// user-entered address, and validating the messages the relay popup postMessages back. No DOM/React —
// host-tested (liveRelay.test.ts). The relay popup itself is the twin's runRelay (tools/live-entry.ts).

/** Normalize a user-entered camera address to a bare host[:port] — strips scheme, path, whitespace, and
 *  rejects anything that can't be a host (injection-safe, since it goes into a popup URL). null = invalid. */
export function normalizeCameraHost(input: string): string | null {
  let h = (input || "").trim();
  if (!h) return null;
  h = h.replace(/^https?:\/\//i, "").replace(/\/.*$/, ""); // drop scheme + any path
  if (!/^[a-zA-Z0-9.-]+(:\d{1,5})?$/.test(h)) return null; // hostname/IPv4 (+ optional :port) only
  return h;
}

/** Build the relay popup URL: http://<host>/live?relay=<portal origin>&p=<preset>. null if host invalid. */
export function buildRelayUrl(host: string, portalOrigin: string, preset: number): string | null {
  const h = normalizeCameraHost(host);
  if (!h) return null;
  const p = preset >= 0 && preset < 4 ? preset : 1; // default XGA
  return "http://" + h + "/live?relay=" + encodeURIComponent(portalOrigin) + "&p=" + p;
}

export type RelayMessage =
  | { type: "ready" }
  | { type: "frame"; buf: ArrayBuffer }
  | { type: "error" };

/** Validate + narrow a postMessage payload from the relay popup. Returns null for anything unrecognized.
 *  The caller MUST ALSO check event.origin against the connected camera before trusting a frame. */
export function parseRelayMessage(data: unknown): RelayMessage | null {
  if (!data || typeof data !== "object") return null;
  const d = data as { type?: unknown; buf?: unknown };
  if (d.type === "pixie-relay-ready") return { type: "ready" };
  if (d.type === "pixie-relay-error") return { type: "error" };
  if (d.type === "pixie-frame" && d.buf instanceof ArrayBuffer) return { type: "frame", buf: d.buf };
  return null;
}
